type DropPositionOptions<TItem> = {
  fromColumnId: string;
  toColumnId: string;
  overId: string;
  activeId: string;
  /** Target column's order BEFORE the drag started (no-op detection). */
  originalItems: TItem[];
  /**
   * Target column's RENDERED order at drop time (the mid-drag snapshot). For
   * cross-column drags this already contains the active item at its entry
   * slot; `overSortableIndex` is an index into this same list.
   */
  targetSnapshotItems: TItem[];
  getItemId: (item: TItem) => string;
  overSortableIndex?: unknown;
};

export type DropPosition<TItem> = {
  finalItems: TItem[];
  beforeItemId: string | null;
  afterItemId: string | null;
};

/** True when both lists hold the same item ids in the same order. */
export function sameIdOrder<TItem>(
  a: readonly TItem[],
  b: readonly TItem[],
  getItemId: (item: TItem) => string
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (getItemId(a[i]!) !== getItemId(b[i]!)) return false;
  }
  return true;
}

function toSortableIndex(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function moveItem<TItem>(items: TItem[], fromIndex: number, toIndex: number): TItem[] {
  const next = [...items];
  const [item] = next.splice(fromIndex, 1);
  if (item === undefined) return items;
  next.splice(toIndex, 0, item);
  return next;
}

/**
 * Derives the final order of the drop's target column — and the neighbor ids
 * to persist — from what the user actually SAW at release.
 *
 * Both same- and cross-column drops resolve against `targetSnapshotItems`
 * (the rendered order): the sortable preview shifts neighbors as if the
 * active item moved to `over`'s index, so `arrayMove(snapshot, oldIdx,
 * overSortableIndex)` reproduces the preview exactly. Resolving cross-column
 * drops from the snapshot's entry slot alone (the old behavior) persisted
 * the slot where the card ENTERED the column, not where it was released.
 */
export function deriveDropPosition<TItem>({
  fromColumnId,
  toColumnId,
  overId,
  activeId,
  originalItems,
  targetSnapshotItems,
  getItemId,
  overSortableIndex,
}: DropPositionOptions<TItem>): DropPosition<TItem> | null {
  const isColumnDrop = overId.startsWith('column-');
  const base = targetSnapshotItems;

  const oldIdx = base.findIndex((it) => getItemId(it) === activeId);
  if (oldIdx < 0) return null;

  const sortableIndex = toSortableIndex(overSortableIndex);
  let newIdx: number;
  if (isColumnDrop || sortableIndex === null) {
    // Same-column: append (dropping on the column body past the last card).
    // Cross-column: keep the snapshot slot — hovering the column droppable
    // applies no sortable transforms, so the snapshot order IS the preview.
    newIdx = fromColumnId === toColumnId ? base.length - 1 : oldIdx;
  } else {
    newIdx = sortableIndex;
  }

  const finalItems = newIdx === oldIdx ? base : moveItem(base, oldIdx, newIdx);

  // No-op guard against the PRE-DRAG order, not snapshot indices — a drag
  // that left the column and came back (A→B→A) has a snapshot that already
  // diverges from the original.
  if (fromColumnId === toColumnId && sameIdOrder(finalItems, originalItems, getItemId)) {
    return null;
  }

  const dropIndex = finalItems.findIndex((it) => getItemId(it) === activeId);
  if (dropIndex < 0) return null;

  const targetItems = finalItems.filter((it) => getItemId(it) !== activeId);
  const beforeItem = targetItems[dropIndex] ?? null;
  const afterItem = dropIndex > 0 ? (targetItems[dropIndex - 1] ?? null) : null;

  return {
    finalItems,
    beforeItemId: beforeItem ? getItemId(beforeItem) : null,
    afterItemId: afterItem ? getItemId(afterItem) : null,
  };
}
